import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button } from "@narmi/design_system";
import {
  ContextForm,
  Dialog,
  useFormData,
  useNotificationContext,
} from "cerulean";
import {
  presetPeriods,
  Period,
} from "byzantine/src/TransactionSearchAndFilter";
import DateRangeFormField from "../form/DateRangeFormField";
import DropdownField from "../form/DropdownField";
import {
  downloadTransactions,
  formatDateForApi,
} from "../DownloadTransactionsDialog/transactionDownloadHelpers";

const FILE_FORMATS = [
  { displayName: "Spreadsheet (CSV)", value: "csv" },
  { displayName: "Microsoft money (OFX v1)", value: "ofx" },
  { displayName: "Personal finance (QIF)", value: "qif" },
  { displayName: "Quickbooks (QBO)", value: "qbo" },
  { displayName: "Quicken (QFX)", value: "qfx" },
];

interface DownloadFormProps {
  closeDialog: () => void;
  endpoint: string;
}

type Format = "csv" | "ofx" | "qif" | "qbo" | "qfx";

type DownloadFormData = {
  min_date?: string;
  max_date?: string;
  period?: Period;
  format?: Format;
};

const DownloadForm = ({ closeDialog, endpoint }: DownloadFormProps) => {
  const { l10n } = useLocalization();
  const { formData, setFormData, onChange } = useFormData({});
  const { sendNotificationToParent } = useNotificationContext();

  useEffect(() => {
    if (formData?.period !== "custom") {
      setFormData((prevState: DownloadFormData) => {
        const newState = { ...prevState };
        delete newState.min_date;
        delete newState.max_date;
        return newState;
      });
    }
  }, [formData?.period]);

  const onSubmit = async (callback: (error?: Error) => void) => {
    closeDialog();

    try {
      let minDate: string | undefined;
      let maxDate: string | undefined;

      if (formData.period === "custom") {
        minDate = formatDateForApi(formData?.min_date);
        maxDate = formatDateForApi(formData?.max_date);
      } else if (formData.period) {
        const [periodStart, periodEnd] = formData.period;
        minDate = formatDateForApi(periodStart);
        maxDate = formatDateForApi(periodEnd);
      }

      await downloadTransactions({
        accountId: endpoint
          .replace("accounts/", "")
          .replace("/transactions_download", ""),
        fileFormat: formData.file_format,
        minDate,
        maxDate,
        period: formData.period === "custom" ? "custom" : undefined,
      });

      sendNotificationToParent({
        type: "success",
        text: l10n.getString(
          "notification-download-started",
          null,
          "Download started."
        ),
      });
      callback();
    } catch (error) {
      callback(error as Error);
    }
  };

  return (
    <ContextForm data={formData} onChange={onChange}>
      <ContextForm.Field required>
        <DropdownField
          options={FILE_FORMATS}
          field="file_format"
          label={l10n.getString("label-file-type", null, "File type")}
        />
      </ContextForm.Field>
      <ContextForm.Field required>
        <DropdownField
          options={presetPeriods()}
          field="period"
          label={l10n.getString("label-date-range", null, "Date range")}
        />
      </ContextForm.Field>
      <DateRangeFormField
        hidden={formData?.period !== "custom"}
        required={true}
        allowManualEntry={true}
      />
      <ContextForm.ActionBar>
        <ContextForm.Action
          noValidation
          onSubmit={closeDialog}
          dangerouslyDisableShowLoading
        >
          <div style={{ margin: "auto var(--space-m) auto auto" }}>
            <Button
              kind="negative"
              label={l10n.getString("button-cancel", null, "Cancel")}
              testId="download-cancel"
            />
          </div>
        </ContextForm.Action>
        <ContextForm.Action onSubmit={onSubmit}>
          <Button
            kind="primary"
            label={l10n.getString("button-download", null, "Download")}
            testId="download-submit"
          />
        </ContextForm.Action>
      </ContextForm.ActionBar>
    </ContextForm>
  );
};

interface TransactionsDownloaderProps {
  accountUuid: string;
}
const TransactionsDownloader = ({
  accountUuid,
}: TransactionsDownloaderProps) => {
  const { l10n } = useLocalization();
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const closeDialog = () => {
    setIsDownloadDialogOpen(false);
  };
  const openDialog = () => {
    setIsDownloadDialogOpen(true);
  };
  const endpoint = `accounts/${accountUuid}/transactions_download`;

  return (
    <>
      <Button
        kind="plain"
        label={l10n.getString("button-download", null, "Download")}
        testId="open-download-dialog"
        onClick={openDialog}
      />
      <Dialog
        isOpen={isDownloadDialogOpen}
        onUserDismiss={closeDialog}
        title={l10n.getString(
          "dialog-title-download-transaction-history",
          null,
          "Download transaction history"
        )}
      >
        <div className="margin--top--s">
          <DownloadForm closeDialog={closeDialog} endpoint={endpoint} />
        </div>
      </Dialog>
    </>
  );
};
export default TransactionsDownloader;
